import React, { useState, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TableSortLabel, Alert, Paper, Divider,
} from '@mui/material';
import { Download, CloudUpload, InfoOutlined } from '@mui/icons-material';

const C = {
  blue:   '#005EB8',
  green:  '#009639',
  yellow: '#FFB81C',
  red:    '#DA291C',
  dark:   '#425563',
  mid:    '#768692',
  pale:   '#E8EDEE',
  white:  '#FFFFFF',
};

function utilFg(pct) {
  if (pct >= 80) return C.green;
  if (pct >= 60) return '#7B5800';
  return C.red;
}
function utilBg(pct) {
  if (pct >= 80) return '#E8F5E9';
  if (pct >= 60) return '#FFF8E1';
  return '#FFEBEE';
}
function dnaFg(pct) {
  if (pct <= 10) return C.green;
  if (pct <= 25) return '#7B5800';
  return C.red;
}
function dnaBg(pct) {
  if (pct <= 10) return '#E8F5E9';
  if (pct <= 25) return '#FFF8E1';
  return '#FFEBEE';
}

// ─── CSV parsing ──────────────────────────────────────────────────────────────

function parseVccCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) throw new Error('CSV must have a header row and at least one data row');

  const hdrs = lines[0].split(',').map(h =>
    h.trim().replace(/^"|"$/g, '').toLowerCase().replace(/[\s/\-()+]+/g, '_')
  );

  const get = (vals, ...keys) => {
    for (const k of keys) {
      const i = hdrs.indexOf(k);
      if (i >= 0 && vals[i] !== undefined && vals[i].trim() !== '') return vals[i].trim();
    }
    return '';
  };

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));

    const period    = get(vals, 'period', 'week', 'month', 'date', 'week_commencing', 'week_starting', 'week_beginning');
    const available = parseInt(get(vals, 'available', 'total', 'total_workstations', 'workstations_available', 'capacity')) || 0;
    const allocated = parseInt(get(vals, 'allocated', 'workstations_allocated', 'assigned', 'booked')) || 0;
    const used      = parseInt(get(vals, 'used', 'workstations_used', 'utilised', 'attended', 'occupied')) || 0;
    const dnaRaw    = parseInt(get(vals, 'dna', 'did_not_attend', 'non_use', 'non_attendance', 'absent', 'dna_non_use', 'dna/non_use')) || 0;

    if (!period && available === 0 && allocated === 0) continue;

    const dna          = dnaRaw > 0 ? dnaRaw : Math.max(0, allocated - used);
    const alloc_pct    = available > 0 ? +((allocated  / available) * 100).toFixed(1) : 0;
    const util_pct     = allocated  > 0 ? +((used       / allocated) * 100).toFixed(1) : 0;
    const overall_util = available  > 0 ? +((used       / available) * 100).toFixed(1) : 0;
    const dna_pct      = allocated  > 0 ? +((dna        / allocated) * 100).toFixed(1) : 0;

    rows.push({ period, available, allocated, used, dna, alloc_pct, util_pct, overall_util, dna_pct });
  }

  if (!rows.length) throw new Error('No valid data rows found. Check that your CSV has the required columns.');
  return rows;
}

// ─── Template + export ────────────────────────────────────────────────────────

function downloadTemplate() {
  const csv = [
    'Period,Available,Allocated,Used,DNA',
    '2026-W14 (Apr 1),20,18,15,3',
    '2026-W15 (Apr 8),20,17,16,1',
    '2026-W16 (Apr 15),20,19,14,5',
    '2026-W17 (Apr 22),20,18,17,1',
  ].join('\n');
  trigger(new Blob([csv], { type: 'text/csv' }), 'vcc-utilisation-template.csv');
}

function exportCSV(rows) {
  const hdr = 'Period,Available,Allocated,Allocation %,Used,Utilisation %,DNA/Non-use,DNA/Non-use %,Overall Utilisation %';
  const body = rows.map(r =>
    [r.period, r.available, r.allocated, r.alloc_pct, r.used, r.util_pct, r.dna, r.dna_pct, r.overall_util]
      .map(v => `"${v}"`).join(',')
  );
  trigger(new Blob([[hdr, ...body].join('\n')], { type: 'text/csv' }), 'vcc-utilisation.csv');
}

function trigger(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

// ─── SVG chart ────────────────────────────────────────────────────────────────

const VB_W = 900, VB_H = 200;
const PAD  = { top: 24, right: 80, bottom: 52, left: 44 };
const CW   = VB_W - PAD.left - PAD.right;
const CH   = VB_H - PAD.top  - PAD.bottom;

function UtilChart({ rows }) {
  if (!rows.length) return null;
  const n    = rows.length;
  const barW = Math.min(55, (CW / n) * 0.55);
  const gap  = (CW - n * barW) / (n + 1);
  const barX = i => PAD.left + gap + i * (barW + gap);
  const yAt  = v => PAD.top + CH - (v / 100) * CH;

  return (
    <Box>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', overflow: 'visible' }}>

        {[0, 20, 40, 60, 80, 100].map(v => (
          <g key={v}>
            <line x1={PAD.left} y1={yAt(v)} x2={PAD.left + CW} y2={yAt(v)}
              stroke={v === 0 ? C.dark : C.pale} strokeWidth={v === 0 ? 1 : 0.5} />
            <text x={PAD.left - 5} y={yAt(v) + 4} textAnchor="end" fontSize={9} fill={C.mid}>{v}%</text>
          </g>
        ))}

        <line x1={PAD.left} y1={yAt(80)} x2={PAD.left + CW} y2={yAt(80)}
          stroke={C.green} strokeWidth={1} strokeDasharray="4 3" opacity={0.6} />
        <text x={PAD.left + CW + 4} y={yAt(80) + 4} fontSize={8} fill={C.green} opacity={0.8}>80% target</text>

        {rows.map((r, i) => {
          const x     = barX(i);
          const h     = (r.util_pct / 100) * CH;
          const color = utilFg(r.util_pct);
          const lbl   = r.period.length > 10 ? r.period.slice(0, 9) + '…' : r.period;
          return (
            <g key={i}>
              <rect x={x} y={yAt(r.util_pct)} width={barW} height={h}
                fill={color} opacity={0.82} rx={2}>
                <title>{r.period}: {r.util_pct}% utilised ({r.used}/{r.allocated})</title>
              </rect>
              {h > 14 && (
                <text x={x + barW / 2} y={yAt(r.util_pct) - 5}
                  textAnchor="middle" fontSize={9} fontWeight="600" fill={color}>
                  {r.util_pct}%
                </text>
              )}
              <text x={x + barW / 2} y={PAD.top + CH + 16}
                textAnchor="middle" fontSize={8.5} fill={C.mid}>{lbl}</text>
            </g>
          );
        })}

        <rect x={PAD.left} y={PAD.top} width={CW} height={CH}
          fill="none" stroke={C.pale} strokeWidth={1} />
      </svg>

      <Box sx={{ display: 'flex', gap: 2.5, mt: 0.5, pl: 0.5, flexWrap: 'wrap' }}>
        {[[C.green, '≥80% — on target'], [C.yellow, '60–79% — below target'], [C.red, '<60% — significantly under-utilised']].map(([c, l]) => (
          <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, bgcolor: c, borderRadius: '2px', opacity: 0.82 }} />
            <Typography sx={{ fontSize: '0.65rem', color: C.dark }}>{l}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── Summary card ─────────────────────────────────────────────────────────────

function StatCard({ label, primary, secondary, accentColor }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, minWidth: 140, p: 2, borderRadius: 2,
      border: `1.5px solid ${accentColor}30`,
      background: `linear-gradient(135deg, ${accentColor}08 0%, ${C.white} 100%)`,
    }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.8, color: C.mid, mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1, color: accentColor }}>
        {primary}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: '0.7rem', color: C.mid, mt: 0.5 }}>{secondary}</Typography>
      )}
    </Paper>
  );
}

// ─── Data table ───────────────────────────────────────────────────────────────

const COLS = [
  { id: 'period',       label: 'Period',               align: 'left'  },
  { id: 'available',    label: 'Available',             align: 'right' },
  { id: 'allocated',    label: 'Allocated',             align: 'right' },
  { id: 'alloc_pct',    label: 'Allocation %',          align: 'right' },
  { id: 'used',         label: 'Used',                  align: 'right' },
  { id: 'util_pct',     label: 'Utilisation %',         align: 'right' },
  { id: 'dna',          label: 'DNA / Non-use',         align: 'right' },
  { id: 'dna_pct',      label: 'DNA / Non-use %',       align: 'right' },
  { id: 'overall_util', label: 'Overall Utilisation %', align: 'right' },
];

function DataTable({ rows }) {
  const [sortKey, setSortKey] = useState('period');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = key => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir(key === 'period' ? 'asc' : 'desc'); }
  };

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const va = a[sortKey] ?? '', vb = b[sortKey] ?? '';
    if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === 'asc' ? va - vb : vb - va;
  }), [rows, sortKey, sortDir]);

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: C.blue }}>
            {COLS.map(col => (
              <TableCell key={col.id} align={col.align}
                sortDirection={sortKey === col.id ? sortDir : false}
                sx={{ py: 1, whiteSpace: 'nowrap', borderBottom: 'none' }}>
                <TableSortLabel
                  active={sortKey === col.id}
                  direction={sortKey === col.id ? sortDir : 'asc'}
                  onClick={() => handleSort(col.id)}
                  sx={{
                    color: `${C.white} !important`,
                    '& .MuiTableSortLabel-icon': { color: `${C.white} !important`, opacity: 0.65 },
                    fontSize: '0.68rem', fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                  }}>
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, i) => (
            <TableRow key={i} hover sx={{ backgroundColor: i % 2 === 0 ? C.white : '#F7FAFC' }}>
              <TableCell sx={{ fontWeight: 500, fontSize: '0.78rem' }}>{row.period}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{row.available}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{row.allocated}</TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: utilFg(row.alloc_pct), bgcolor: utilBg(row.alloc_pct) }}>
                  {row.alloc_pct}%
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem', color: C.green, fontWeight: 600 }}>
                {row.used}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: utilFg(row.util_pct), bgcolor: utilBg(row.util_pct) }}>
                  {row.util_pct}%
                </Box>
              </TableCell>
              <TableCell align="right" sx={{
                fontSize: '0.78rem',
                color: row.dna > 0 ? C.red : C.mid,
                fontWeight: row.dna > 0 ? 600 : 400,
              }}>
                {row.dna}
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: dnaFg(row.dna_pct), bgcolor: dnaBg(row.dna_pct) }}>
                  {row.dna_pct}%
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1,
                  fontSize: '0.78rem', fontWeight: 700,
                  color: utilFg(row.overall_util), bgcolor: utilBg(row.overall_util) }}>
                  {row.overall_util}%
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function VccUtilisationReport() {
  const fileRef              = useRef(null);
  const [rows,     setRows]  = useState(null);
  const [error,    setError] = useState('');
  const [fileName, setFile]  = useState('');

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    setFile(file.name);
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        setRows(parseVccCSV(ev.target.result));
        setError('');
      } catch (err) {
        setError(err.message);
        setRows(null);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const summary = useMemo(() => {
    if (!rows?.length) return null;
    const totAvail = rows.reduce((s, r) => s + r.available, 0);
    const totAlloc = rows.reduce((s, r) => s + r.allocated, 0);
    const totUsed  = rows.reduce((s, r) => s + r.used, 0);
    const totDna   = rows.reduce((s, r) => s + r.dna, 0);
    return {
      avgAlloc:   totAvail > 0 ? +((totAlloc / totAvail) * 100).toFixed(1) : 0,
      avgUtil:    totAlloc > 0 ? +((totUsed  / totAlloc) * 100).toFixed(1) : 0,
      avgDna:     totAlloc > 0 ? +((totDna   / totAlloc) * 100).toFixed(1) : 0,
      avgOverall: totAvail > 0 ? +((totUsed  / totAvail) * 100).toFixed(1) : 0,
      totAlloc, totUsed, totDna, periods: rows.length,
    };
  }, [rows]);

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: C.blue }}>
          Virtual Call Centre Utilisation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Workstation allocation vs actual use — DNA and non-utilisation rates monitored by the OPRA team.
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <input type="file" accept=".csv" ref={fileRef} style={{ display: 'none' }} onChange={handleFile} />
        <Button variant="contained" size="small"
          startIcon={<CloudUpload sx={{ fontSize: '16px !important' }} />}
          onClick={() => fileRef.current.click()}
          sx={{ bgcolor: C.blue, '&:hover': { bgcolor: '#004494' }, px: 2.5, py: 0.7,
            fontSize: '0.78rem', textTransform: 'none', fontWeight: 700 }}>
          Upload CSV
        </Button>
        <Button variant="outlined" size="small"
          startIcon={<Download sx={{ fontSize: '14px !important' }} />}
          onClick={downloadTemplate}
          sx={{ borderColor: C.blue, color: C.blue, fontSize: '0.7rem',
            py: 0.4, px: 1.5, textTransform: 'none' }}>
          Download Template
        </Button>
        {fileName && (
          <Typography variant="caption" sx={{ color: C.mid }}>
            {fileName} · {rows?.length ?? 0} row{rows?.length !== 1 ? 's' : ''} loaded
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>{error}</Alert>}

      {summary && rows && (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            <StatCard
              label="Avg Allocation Rate"
              primary={`${summary.avgAlloc}%`}
              secondary={`${summary.totAlloc.toLocaleString()} workstation-sessions allocated`}
              accentColor={utilFg(summary.avgAlloc)}
            />
            <StatCard
              label="Avg Utilisation (of allocated)"
              primary={`${summary.avgUtil}%`}
              secondary={`${summary.totUsed.toLocaleString()} of ${summary.totAlloc.toLocaleString()} used`}
              accentColor={utilFg(summary.avgUtil)}
            />
            <StatCard
              label="DNA / Non-use Rate"
              primary={`${summary.avgDna}%`}
              secondary={`${summary.totDna.toLocaleString()} workstation-sessions unused`}
              accentColor={dnaFg(summary.avgDna)}
            />
            <StatCard
              label="Overall Utilisation"
              primary={`${summary.avgOverall}%`}
              secondary={`${summary.periods} period${summary.periods !== 1 ? 's' : ''} analysed`}
              accentColor={utilFg(summary.avgOverall)}
            />
          </Box>

          {/* Chart */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, p: 2, mb: 2 }}>
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark }}>
                Utilisation % by Period
              </Typography>
              <Typography variant="caption" sx={{ color: C.mid }}>
                Dashed line = 80% target · % of allocated workstations actually used
              </Typography>
            </Box>
            <UtilChart rows={rows} />
          </Paper>

          {/* Table */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.pale}`, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: C.dark }}>
                Breakdown by Period
              </Typography>
              <Button size="small" variant="outlined"
                startIcon={<Download sx={{ fontSize: '14px !important' }} />}
                onClick={() => exportCSV(rows)}
                sx={{ borderColor: C.blue, color: C.blue, fontSize: '0.7rem',
                  py: 0.4, px: 1.5, textTransform: 'none' }}>
                CSV
              </Button>
            </Box>
            <Divider />
            <DataTable rows={rows} />
          </Paper>

          <Alert severity="info" icon={<InfoOutlined sx={{ fontSize: 18 }} />}
            sx={{ fontSize: '0.75rem', '& .MuiAlert-message': { pt: '2px' } }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', mb: 0.25 }}>Note on data</Typography>
            <Typography sx={{ fontSize: '0.72rem' }}>
              Data is collected and maintained by the OPRA team. Upload a CSV with columns:
              {' '}<em>Period, Available, Allocated, Used, DNA</em>.
              If DNA is omitted it is calculated as Allocated − Used.
              Download the template for the expected column format.
            </Typography>
          </Alert>
        </>
      )}

      {!rows && !error && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: 180, color: C.mid, gap: 1,
          border: `1.5px dashed ${C.pale}`, borderRadius: 2, mt: 1,
        }}>
          <CloudUpload sx={{ fontSize: 40, opacity: 0.3 }} />
          <Typography variant="body2">Upload a CSV file to view the report</Typography>
          <Typography variant="caption" sx={{ color: C.pale }}>
            Expected columns: Period, Available, Allocated, Used, DNA
          </Typography>
        </Box>
      )}
    </Box>
  );
}
